/**
 * Dashboard — Financial Metrics Cards row (Assets, Liabilities, Equity, Net Income)
 */

import { Card, Skeleton } from '@elsapiens/ui'
import { Link } from 'react-router-dom'

import { formatMoney } from '../../utils/formatters'

import type { FinancialMetrics } from './dashboard-helpers'

interface FinancialMetricsCardsProps {
  isLoading: boolean
  metrics: FinancialMetrics
  currency: string
}

interface MetricCardProps {
  title: string
  amount: number
  currency: string
  isLoading: boolean
  caption: string
  iconBg: string
  iconPath: string
  to: string
  valueClassName?: string
}

function MetricCard({ title, amount, currency, isLoading, caption, iconBg, iconPath, to, valueClassName }: MetricCardProps): JSX.Element {
  return (
    <Link to={to}>
      <Card className="h-full hover:shadow-md transition-shadow" padding="sm">
        <div className="flex items-center justify-between" style={{ minHeight: '3.5rem' }}>
          <div className="min-w-0 flex-1">
            <p className="text-sm text-foreground-secondary truncate">{title}</p>
            {isLoading ? (
              <Skeleton className="el-mt-xs" height="1.5rem" width="100px" />
            ) : (
              <p className={`text-xl font-semibold mt-0.5 truncate ${valueClassName ?? ''}`}>
                {formatMoney({ amount, currency })}
              </p>
            )}
            <p className="text-xs text-foreground-secondary mt-0.5 truncate">{caption}</p>
          </div>
          <div className={`p-2.5 rounded-lg flex-shrink-0 ml-3 ${iconBg}`}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d={iconPath} strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
            </svg>
          </div>
        </div>
      </Card>
    </Link>
  )
}

interface MetricSpec {
  title: string
  amount: number
  caption: string
  iconBg: string
  iconPath: string
  to: string
  valueClassName?: string
}

function buildMetricSpecs(metrics: FinancialMetrics): MetricSpec[] {
  const { totalAssets, totalLiabilities, totalEquity, netIncome } = metrics
  const netIncomeBg = netIncome >= 0
    ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400'
    : 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400'
  const netIncomeClass = netIncome >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
  return [
    { title: 'Total Assets', amount: totalAssets, caption: 'From Balance Sheet',
      iconBg: 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400',
      iconPath: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
      to: '/reports/balance-sheet' },
    { title: 'Total Liabilities', amount: totalLiabilities, caption: 'From Balance Sheet',
      iconBg: 'bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-400',
      iconPath: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
      to: '/reports/balance-sheet' },
    { title: 'Total Equity', amount: totalEquity, caption: 'Assets - Liabilities',
      iconBg: 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400',
      iconPath: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z',
      to: '/reports/balance-sheet' },
    { title: 'Net Income', amount: netIncome, caption: 'Revenue - Expenses',
      iconBg: netIncomeBg,
      iconPath: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
      to: '/reports/income-statement',
      valueClassName: netIncomeClass },
  ]
}

export function FinancialMetricsCards({ isLoading, metrics, currency }: FinancialMetricsCardsProps): JSX.Element {
  const specs = buildMetricSpecs(metrics)
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 el-gap-md el-mb-lg">
      {specs.map(spec => (
        <MetricCard key={spec.title} {...spec} currency={currency} isLoading={isLoading} />
      ))}
    </div>
  )
}
